import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import screenshotApp from "../assets/img/screenshotapp.png";
import NavbarFooter from "../components/Footer";
import useTranslation, { AllowedLanguages } from "../hooks/useTranslation";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { StartLearningOnboardingFlow } from "./start-learning";

export const Route = createFileRoute("/")({
  validateSearch: (search: Record<string, unknown>) => ({
    onboarding: search.onboarding === "1" ? "1" : undefined,
  }),
  component: HomePage,
});

function HomePage() {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const onboardingOpen = search.onboarding === "1";

  const [localStorageLanguage, setLocalStorageLanguage] =
    useLocalStorage<AllowedLanguages>("local-language", "ENGLISH");

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocalStorageLanguage(e.target.value as AllowedLanguages);
  };

  const openOnboarding = () => {
    navigate({ to: "/", search: { onboarding: "1" } });
  };

  const topics = [
    "landingpage_archive_card_content_topic_architecture",
    "landingpage_archive_card_content_topic_film",
    "landingpage_archive_card_content_topic_science",
    "landingpage_archive_card_content_topic_music",
    "landingpage_archive_card_content_topic_food",
    "landingpage_archive_card_content_topic_history",
    "landingpage_archive_card_content_topic_design",
    "landingpage_archive_card_content_topic_technology",
    "landingpage_archive_card_content_topic_philosophy",
  ] as const;

  const wordBank = [
    {
      wordKey: "landingpage_archive_card_lexicon_word_1",
      translationKey: "landingpage_archive_card_lexicon_translation_1",
      contextKey: "landingpage_archive_card_lexicon_context_1",
    },
    {
      wordKey: "landingpage_archive_card_lexicon_word_2",
      translationKey: "landingpage_archive_card_lexicon_translation_2",
      contextKey: "landingpage_archive_card_lexicon_context_2",
    },
    {
      wordKey: "landingpage_archive_card_lexicon_word_3",
      translationKey: "landingpage_archive_card_lexicon_translation_3",
      contextKey: "landingpage_archive_card_lexicon_context_3",
    },
  ] as const;

  return (
    <>
      {onboardingOpen && (
        <div className="fixed inset-0 z-[70] flex items-stretch justify-center bg-black/35 p-0 sm:items-center sm:p-5">
          <div className="h-full w-full max-w-none sm:h-auto sm:max-w-4xl">
            <StartLearningOnboardingFlow
              embedded
              onClose={() =>
                navigate({ to: "/", search: { onboarding: undefined } })
              }
            />
          </div>
        </div>
      )}

      <div className="bg-white">
        <div className="mx-auto max-w-[1520px] lg:grid lg:h-screen lg:grid-cols-[340px_minmax(0,1fr)] lg:gap-5 lg:pt-0 lg:pb-5">
          <aside className="order-1 px-4 py-4 sm:px-6 sm:py-6 lg:h-full lg:px-0 lg:pt-5 lg:pb-0">
            <div className="flex h-full flex-col">
              <div>
                <div className="mb-6 flex items-center justify-between gap-4">
                  <nav className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-neutral-500">
                    <Link to="/about" className="hover:text-neutral-900">
                      {translate("website_nav_about")}
                    </Link>
                    <Link to="/pricing" className="hover:text-neutral-900">
                      {translate("website_nav_pricing")}
                    </Link>
                    <Link to="/app" className="hover:text-neutral-900">
                      {translate("website_nav_to_app")}
                    </Link>
                  </nav>
                  <label className="inline-flex items-center gap-2 rounded-full bg-[#f3f3f1] px-3 py-2 text-base text-neutral-700">
                    <GlobeAltIcon className="h-4 w-4 text-neutral-500" />
                    <select
                      aria-label={translate("landingpage_onepager_language_label")}
                      className="bg-transparent text-base text-neutral-800 focus:outline-hidden"
                      value={localStorageLanguage}
                      onChange={handleLanguageChange}
                    >
                      <option value="ENGLISH">EN</option>
                      <option value="GERMAN">DE</option>
                    </select>
                  </label>
                </div>

                <p className="font-serif text-[2rem] leading-none text-neutral-950">
                  {translate("landingpage_archive_brand")}
                </p>

                <div className="mt-7">
                  <p className="max-w-[17rem] text-[1rem] leading-7 text-neutral-700">
                    {translate("landingpage_onepager_intro")}
                  </p>
                  <p className="mt-4 max-w-[17rem] text-[0.96rem] leading-6 text-neutral-600">
                    {translate("landingpage_onepager_supporting")}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={openOnboarding}
                  className="mt-7 inline-flex items-center rounded-[1rem] bg-neutral-950 px-5 py-3 text-base text-white transition-colors hover:bg-neutral-800"
                >
                  {translate("landingpage_archive_cta_start")} &rarr;
                </button>
              </div>
            </div>
          </aside>

          <main className="order-2 px-4 py-4 sm:px-6 sm:py-6 lg:h-full lg:min-h-0 lg:px-0 lg:pt-0 lg:pb-0">
            <div className="space-y-4 lg:grid lg:h-full lg:grid-cols-12 lg:grid-rows-[minmax(0,1.03fr)_minmax(0,0.97fr)] lg:gap-4 lg:space-y-0 lg:pt-5">
                <RabbitHoleStyleCard className="lg:col-span-7 lg:row-span-1">
                  <p className="text-sm text-neutral-500">
                    {translate("landingpage_archive_card_adaptation_kicker")}
                  </p>
                  <h2 className="mt-2 max-w-[12ch] font-serif text-[1.85rem] leading-tight text-slate-950 lg:text-[1.65rem]">
                    {translate("landingpage_archive_card_adaptation_title")}
                  </h2>
                  <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] xl:items-start">
                    <div className="rounded-[0.95rem] bg-white px-4 py-4 lg:py-3.5">
                      <p className="text-sm text-neutral-500">
                        {translate("landingpage_onepager_original_label")}
                      </p>
                      <p className="mt-2 text-[0.97rem] leading-6 text-neutral-700">
                        {translate("landingpage_archive_card_adaptation_original")}
                      </p>
                    </div>
                    <div className="hidden items-center justify-center pt-12 text-3xl text-neutral-400 xl:flex">
                      &rarr;
                    </div>
                    <div className="rounded-[0.95rem] bg-white px-4 py-4 lg:py-3.5">
                      <p className="inline-flex rounded-[0.75rem] bg-[#e9f1e7] px-3 py-1 text-sm text-neutral-700">
                        {translate("landingpage_archive_card_adaptation_adapted_label")}
                      </p>
                      <p className="mt-2 text-[0.97rem] leading-6 text-neutral-700">
                        {translate("landingpage_archive_card_adaptation_adapted")}
                      </p>
                    </div>
                  </div>
                </RabbitHoleStyleCard>

                <RabbitHoleStyleCard className="lg:col-span-5 lg:row-span-1">
                    <p className="text-sm text-neutral-500">
                      {translate("landingpage_archive_card_vocabulary_kicker")}
                    </p>
                    <h2 className="mt-2 max-w-[11ch] font-serif text-[1.8rem] leading-tight text-slate-950 lg:text-[1.55rem]">
                      {translate("landingpage_archive_card_vocabulary_title")}
                    </h2>
                    <div className="mt-4 rounded-[0.95rem] bg-white px-4 py-4 lg:py-3.5">
                      <p className="text-[0.97rem] leading-6 text-neutral-700">
                        {translate("landingpage_archive_card_vocabulary_sentence_prefix")}
                        <span className="rounded-md bg-[#dce7fb] px-1.5 py-0.5 text-neutral-950">
                          {translate("landingpage_archive_card_vocabulary_highlight_word")}
                        </span>
                        {translate("landingpage_archive_card_vocabulary_sentence_suffix")}
                      </p>
                      <div className="mt-4 max-w-[15rem] rounded-[0.9rem] bg-[#f3f3f1] px-4 py-3">
                        <p className="font-serif text-[1.05rem] text-slate-950">
                          {translate("landingpage_archive_card_vocabulary_highlight_word")}
                        </p>
                        <p className="mt-2 text-sm leading-6 text-neutral-600">
                          {translate("landingpage_archive_card_vocabulary_translation_hint")}
                        </p>
                      </div>
                    </div>
                </RabbitHoleStyleCard>

                <RabbitHoleStyleCard className="lg:col-span-4 lg:row-span-1">
                    <p className="text-sm text-neutral-500">
                      {translate("landingpage_archive_card_collection_kicker")}
                    </p>
                    <h2 className="mt-2 max-w-[10ch] font-serif text-[1.8rem] leading-tight text-slate-950 lg:text-[1.55rem]">
                      {translate("landingpage_archive_card_collection_title")}
                    </h2>
                    <div className="mt-4 space-y-3">
                      {wordBank.slice(0, 2).map((entry) => (
                        <div
                          key={entry.wordKey}
                          className="rounded-[0.95rem] bg-white px-4 py-3.5"
                        >
                          <p className="font-serif text-[1rem] leading-tight text-slate-950">
                            {translate(entry.wordKey)}
                          </p>
                          <p className="mt-1 text-sm text-neutral-600">
                            {translate(entry.translationKey)}
                          </p>
                          <p className="mt-2 text-sm leading-5 text-neutral-500">
                            {translate(entry.contextKey)}
                          </p>
                        </div>
                      ))}
                    </div>
                </RabbitHoleStyleCard>

                <RabbitHoleStyleCard className="lg:col-span-3 lg:row-span-1">
                    <p className="text-sm text-neutral-500">
                      {translate("landingpage_archive_card_content_kicker")}
                    </p>
                    <h2 className="mt-2 max-w-[9ch] font-serif text-[1.8rem] leading-tight text-slate-950 lg:text-[1.55rem]">
                      {translate("landingpage_archive_card_content_title")}
                    </h2>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {topics.map((topic) => (
                        <span
                          key={topic}
                          className="rounded-[0.8rem] bg-white px-3 py-2 text-sm text-neutral-700"
                        >
                          {translate(topic)}
                        </span>
                      ))}
                    </div>
                </RabbitHoleStyleCard>

                <RabbitHoleStyleCard className="lg:col-span-5 lg:row-span-1">
                    <p className="text-sm text-neutral-500">
                      {translate("landingpage_onepager_preview_label")}
                    </p>
                    <h2 className="mt-2 max-w-[12ch] font-serif text-[1.8rem] leading-tight text-slate-950 lg:text-[1.55rem]">
                      {translate("landingpage_onepager_preview_title")}
                    </h2>
                    <p className="mt-3 max-w-xl text-[0.97rem] leading-6 text-neutral-700">
                      {translate("landingpage_onepager_preview_body")}
                    </p>
                    <div className="mt-4 overflow-hidden rounded-[0.95rem] bg-white">
                      <img
                        src={screenshotApp}
                        alt={translate("landingpage_screenshot_alt")}
                        className="h-[180px] w-full object-cover object-top lg:h-[148px]"
                      />
                    </div>
                </RabbitHoleStyleCard>
            </div>
          </main>
        </div>
      </div>

      <div className="lg:hidden">
        <NavbarFooter />
      </div>
    </>
  );
}

function RabbitHoleStyleCard({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <article
      className={`rounded-[1.05rem] bg-[#f3f3f1] px-5 py-5 sm:px-6 sm:py-6 lg:h-full lg:px-5 lg:py-4 ${className}`}
    >
      {children}
    </article>
  );
}
